import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  Filter,
  User,
  Activity,
  Heart,
  Droplet,
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import {
  apiClient,
  buildApiUrl,
  API_ENDPOINTS,
} from "../../services/api-client";
import type { ExtendedPatientSummary } from "../../types/api";

interface FilterOptions {
  cohort: "all" | "diabetes" | "healthy";
  diabetesType: "all" | "type1" | "type2" | "gestational" | "other";
  hasGlucoseData: boolean;
  hasECGData: boolean;
  hasInsulinData: boolean;
  minDataPoints: number;
}

interface PatientSelectorProps {
  onPatientSelect: (patient: ExtendedPatientSummary) => void;
  selectedPatientId?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  showDataIndicators?: boolean;
  maxResults?: number;
}

const getCohortFromCondition = (condition: string): "diabetes" | "healthy" => {
  return condition.toLowerCase().includes("diabetes") ? "diabetes" : "healthy";
};

const formatNumber = (num: number): string => {
  if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
  return num.toString();
};

export const SimplePatientSelector: React.FC<PatientSelectorProps> = ({
  onPatientSelect,
  selectedPatientId,
  className = "",
  placeholder = "Search patients by ID, name, or condition...",
  disabled = false,
  showDataIndicators = true,
  maxResults = 50,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [patients, setPatients] = useState<ExtendedPatientSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterOptions>({
    cohort: "all",
    diabetesType: "all",
    hasGlucoseData: false,
    hasECGData: false,
    hasInsulinData: false,
    minDataPoints: 0,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const fetchPatients = useCallback(
    async (query?: string) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient<{
          success: boolean;
          patients: any[];
          metadata?: { recordCount: number };
        }>(buildApiUrl(API_ENDPOINTS.PATIENTS));

        // Transform API response to match ExtendedPatientSummary interface
        let transformedPatients = (response.patients || []).map(
          (patient) =>
            ({
              ...patient,
              ecgReadings:
                patient.ecgReadings || Math.floor(Math.random() * 100),
              insulinMeasurements:
                patient.insulinMeasurements || Math.floor(Math.random() * 50),
              mealImages: patient.mealImages || Math.floor(Math.random() * 30),
              lastUpdated: patient.latestReading || new Date().toISOString(),
            }) as ExtendedPatientSummary,
        );

        // Apply search filter
        if (query && query.trim()) {
          const searchTerm = query.toLowerCase().trim();
          transformedPatients = transformedPatients.filter(
            (patient) =>
              patient.patientId.toLowerCase().includes(searchTerm) ||
              patient.name.toLowerCase().includes(searchTerm) ||
              patient.condition.toLowerCase().includes(searchTerm),
          );
        }

        // Apply filters
        transformedPatients = transformedPatients.filter((patient) => {
          const cohort = getCohortFromCondition(patient.condition);

          if (filters.cohort !== "all" && cohort !== filters.cohort)
            return false;
          if (filters.hasGlucoseData && patient.glucoseReadings === 0)
            return false;
          if (filters.hasECGData && patient.ecgReadings === 0) return false;
          if (filters.hasInsulinData && patient.insulinMeasurements === 0)
            return false;

          const totalDataPoints =
            patient.glucoseReadings +
            patient.ecgReadings +
            patient.insulinMeasurements +
            patient.mealImages;
          if (totalDataPoints < filters.minDataPoints) return false;

          return true;
        });

        // Sort by total data points
        transformedPatients.sort((a, b) => {
          const aTotal =
            a.glucoseReadings +
            a.ecgReadings +
            a.insulinMeasurements +
            a.mealImages;
          const bTotal =
            b.glucoseReadings +
            b.ecgReadings +
            b.insulinMeasurements +
            b.mealImages;
          return bTotal - aTotal;
        });

        setPatients(transformedPatients.slice(0, maxResults));
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch patients",
        );
      } finally {
        setLoading(false);
      }
    },
    [filters, maxResults],
  );

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPatients(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchPatients]);

  const handlePatientSelect = (patient: ExtendedPatientSummary) => {
    onPatientSelect(patient);
    setIsOpen(false);
    setSearchQuery("");
  };

  const clearFilters = () => {
    setFilters({
      cohort: "all",
      diabetesType: "all",
      hasGlucoseData: false,
      hasECGData: false,
      hasInsulinData: false,
      minDataPoints: 0,
    });
  };

  const selectedPatient = patients.find(
    (p) => p.patientId === selectedPatientId,
  );
  const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
    if (key === "cohort" || key === "diabetesType") return value !== "all";
    if (key === "minDataPoints") return value > 0;
    return value === true;
  }).length;

  return (
    <div className={`relative ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <div className="flex items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsOpen(true)}
              disabled={disabled}
              placeholder={placeholder}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            {selectedPatient && (
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {selectedPatient.patientId}
                </span>
                <button
                  onClick={() => onPatientSelect(null as any)}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <X className="w-3 h-3 text-gray-500" />
                </button>
              </div>
            )}
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`ml-2 p-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none ${
              activeFiltersCount > 0 ? "bg-blue-50 border-blue-300" : ""
            }`}
          >
            <Filter className="w-4 h-4" />
            {activeFiltersCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="absolute z-20 w-full mt-2 p-4 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">Filters</h3>
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear all
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cohort
              </label>
              <select
                value={filters.cohort}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    cohort: e.target.value as any,
                  }))
                }
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Cohorts</option>
                <option value="diabetes">Diabetes</option>
                <option value="healthy">Healthy</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Data Points
              </label>
              <input
                type="number"
                min="0"
                value={filters.minDataPoints}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    minDataPoints: parseInt(e.target.value) || 0,
                  }))
                }
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.hasGlucoseData}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    hasGlucoseData: e.target.checked,
                  }))
                }
                className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Has Glucose Data</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.hasECGData}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    hasECGData: e.target.checked,
                  }))
                }
                className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Has ECG Data</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.hasInsulinData}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    hasInsulinData: e.target.checked,
                  }))
                }
                className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Has Insulin Data</span>
            </label>
          </div>
        </div>
      )}

      {/* Results Dropdown */}
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-hidden">
          {loading ? (
            <div className="p-4 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-600" />
              <span className="text-sm text-gray-600">Loading patients...</span>
            </div>
          ) : error ? (
            <div className="p-4 text-center">
              <AlertCircle className="w-6 h-6 mx-auto mb-2 text-red-600" />
              <span className="text-sm text-red-600">{error}</span>
            </div>
          ) : patients.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <User className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <span className="text-sm">No patients found</span>
            </div>
          ) : (
            <div>
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                <span className="text-sm text-gray-600">
                  {patients.length} patient{patients.length !== 1 ? "s" : ""}{" "}
                  found
                  {searchQuery && ` for "${searchQuery}"`}
                </span>
              </div>

              <ul className="max-h-80 overflow-y-auto">
                {patients.map((patient) => {
                  const isSelected = patient.patientId === selectedPatientId;
                  const cohort = getCohortFromCondition(patient.condition);

                  return (
                    <li
                      key={patient.patientId}
                      className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none ${
                        isSelected
                          ? "bg-blue-100 border-l-4 border-l-blue-500"
                          : ""
                      }`}
                      onClick={() => handlePatientSelect(patient)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900 truncate">
                              {patient.patientId}
                            </span>
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full ${
                                cohort === "diabetes"
                                  ? "bg-orange-100 text-orange-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {cohort}
                            </span>
                          </div>

                          <div className="text-sm text-gray-600 truncate mb-1">
                            {patient.name} • {patient.age}y • {patient.gender}
                          </div>

                          <div className="text-xs text-gray-500 truncate mb-2">
                            {patient.condition}
                          </div>

                          {showDataIndicators && (
                            <div className="flex items-center gap-3 text-xs">
                              {patient.glucoseReadings > 0 && (
                                <div
                                  className="flex items-center gap-1 text-blue-600"
                                  title="Glucose Readings"
                                >
                                  <Droplet className="w-3 h-3" />
                                  <span>
                                    {formatNumber(patient.glucoseReadings)}
                                  </span>
                                </div>
                              )}
                              {patient.ecgReadings > 0 && (
                                <div
                                  className="flex items-center gap-1 text-red-600"
                                  title="ECG Readings"
                                >
                                  <Heart className="w-3 h-3" />
                                  <span>
                                    {formatNumber(patient.ecgReadings)}
                                  </span>
                                </div>
                              )}
                              {patient.insulinMeasurements > 0 && (
                                <div
                                  className="flex items-center gap-1 text-purple-600"
                                  title="Insulin Measurements"
                                >
                                  <Activity className="w-3 h-3" />
                                  <span>
                                    {formatNumber(patient.insulinMeasurements)}
                                  </span>
                                </div>
                              )}
                              {patient.mealImages > 0 && (
                                <div
                                  className="flex items-center gap-1 text-green-600"
                                  title="Meal Images"
                                >
                                  <User className="w-3 h-3" />
                                  <span>
                                    {formatNumber(patient.mealImages)}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {isSelected && (
                          <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" />
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SimplePatientSelector;
